import React from "react";
import { EolasFile, getPreSignUrl } from "@eolas-medical/core";

export const DocumentCacheEvents = {
  update: "update-pdf-cache",
  delete: "delete-pdf-cache",
} as const;

type ValueOf<T> = T[keyof T];

export const createPDFEvent = (type: ValueOf<typeof DocumentCacheEvents>, detail?: any) => {
  return new CustomEvent(type, { detail });
};

export const deletePDFCacheItem = (key?: string | null) => {
  if (!key) return;

  window.dispatchEvent(createPDFEvent("delete-pdf-cache", { key }));
};

export const updatePDFCacheItem = (key: string, value: string) => {
  window.dispatchEvent(createPDFEvent("update-pdf-cache", { key, value }));
};

class DocumentCache {
  timeoutCache: Record<string, NodeJS.Timeout>;
  cache: Record<string, string>;
  // clear the cache after 5 minutes
  CACHE_TIME = 1000 * 60 * 5;

  constructor() {
    this.cache = {};
    this.timeoutCache = {};
  }

  getItem(key: string) {
    return this.cache[key];
  }

  deleteEntry(key: string) {
    delete this.cache[key];
  }

  setItem(key: string, value: string) {
    this.cache[key] = value;

    const previousTimeout = this.timeoutCache[key];

    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    const timeout = setTimeout(() => {
      this.deleteEntry(key);
    }, this.CACHE_TIME);

    this.timeoutCache[key] = timeout;
  }
}

const documentCache = new DocumentCache();

export const updateDocumentCache = (file: EolasFile) => {
  if (file.type !== "pdf") return;

  deletePDFCacheItem(file.key);
};

export const useDocumentCache = () => {
  const onDeleteCacheItem = React.useCallback((event: any) => {
    documentCache.deleteEntry(event.detail.key);
  }, []);

  const onUpdateCacheItem = React.useCallback((event: any) => {
    documentCache.setItem(event.detail.key, event.detail.value);
  }, []);

  React.useEffect(() => {
    window.addEventListener(DocumentCacheEvents.delete, onDeleteCacheItem);
    window.addEventListener(DocumentCacheEvents.update, onUpdateCacheItem);

    return () => {
      window.removeEventListener(DocumentCacheEvents.delete, onDeleteCacheItem);
      window.removeEventListener(DocumentCacheEvents.update, onUpdateCacheItem);
    };
  }, [onDeleteCacheItem, onUpdateCacheItem]);
};

export const getPDFUrl = (key?: string | null): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    if (!key) {
      return reject("Missing file key.");
    }

    // return value from the cache
    const cacheValue = documentCache.getItem(key);

    if (cacheValue) {
      return resolve(cacheValue);
    }

    // fetch pdf url and update the cache
    try {
      const s3Url = await getPreSignUrl(key);
      documentCache.setItem(key, s3Url);

      resolve(s3Url);
    } catch (err: any) {
      reject(err);
    }
  });
};
